import { useEffect, useState } from 'react';
import { CallbackObj, CIAMContextType, CIAMSession } from '../types';
import { CIAMAuthContext } from '../context';
import { KeepLive } from '../session/KeepLive';
import { getRouterUrl, getSessionSupportUrl, getSession, logoutSession, deleteCookies, parseCookieString, convertStringToObject, addQueryParamsIfMissing, parseUserData, toURLEncoded } from '../utils/Utils';
import { jwtDecode } from 'jwt-decode';
import { LogoutModuleLoader } from '../session/LogoutModuleLoader';
import '../auth/LoadingSpinner.css'; // Import the CSS for the spinner
import USBLoadingSpinner from '@usb-shield/react-loading-spinner';
import CIAMAuthModal from './CIAMAuthModal';
import useSiteCat, { type Metadata } from '@ciam/analytics';
import { SC_CONFIG } from '../constants/constants';

const CIAMAuthProvider = ({ config, children }: any) => {
  let initialCiamAuth: CIAMContextType = { config };
  const [ciamAuth, setCiamAuth] = useState(initialCiamAuth);
  const [state, setState] = useState("Loading");
  const channel = new BroadcastChannel('session-channel');
  const [logOutLoad, setLogOutLoad] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [loadChallengeModule, setLoadChallengeModule] = useState(false);
  const [callbackObj, setCallbackObj] = useState<any>();
  const [skipLogoutRedirect, setSkipLogoutRedirect] = useState(false);
  const [inactivity, setInactivity] = useState(false);

  const { tag } = useSiteCat(SC_CONFIG(config));
  
  // Modal overlay styles
  const iframeStyles: React.CSSProperties = {
    position: 'absolute',
    zIndex: '99999',
    display: showIframe ? 'flex' : 'none',
    height: '100%',
    width: '100%',
  };


  const loadChallenge = (callbackObj: CallbackObj | undefined) => {
    setLoadChallengeModule(true);
    setCallbackObj(callbackObj)
  }

  const checkWhitelist = (): boolean => {
    const whitelist: string[] = (config && config.whitelistUrls) || [];
    const { pathname } = window.location;
    return whitelist.some(path => pathname.includes(path));
  }
  const reverseSso = async (responseObj: any, onValidationSuccess: (responseObj: any) => void, onValidationFailure: (errorObj: any, jwtResponseObject: any) => void) => {
    console.log("Reverse SSO called");
    const jwtToken = responseObj?.token;
    if (ciamAuth.config.enableCrossDomain) replaceCookieValueFromSessionStorage(jwtToken);
    else {
      const payload = { 
        jwtToken: jwtToken, 
        currentSession: toURLEncoded(JSON.parse(sessionStorage.getItem("currentSession") || '{}')),
        users: toURLEncoded(JSON.parse(localStorage.getItem('users') || '[]'))
      };
      await fetch(getRouterUrl(ciamAuth.config), 
      { method: 'POST',
        credentials: 'include', 
        headers: {'Content-Type': 'application/json', 'Correlation-Id': 'manual-id' }, 
        body: JSON.stringify(payload)
      });
    }
    initOrValidateCIAMAuthSession(false)
    .then((ciamSession) => {
      onValidationSuccess(responseObj);
      // after reverseSSO need to init back
      ciamAuth.config.initCallBack && ciamAuth.config.initCallBack(ciamSession as CIAMSession);
    })
    .catch((e) => {
      console.error('Failed to fetch user session after reverse SSO. Navigate to Login page', e);
      setState("Error");
      onValidationFailure(e, responseObj);
      window.location.href = ciamAuth.config.appLogin + '?targetApp=' + ciamAuth.config.appId;
    });
  }

  const onChallengeAuthSuccess = (response: any) => {
    console.log('CIAM Auth provider - SUCCESS', response);
    returnResponseBackToHost(response);
  }

  const onChallengeAuthFailure = (response: any) => {
    console.log('CIAM Auth provider - FAILURE', response);
    returnResponseBackToHost(response);
  }

  const returnResponseBackToHost = (response: any) => {
    if (callbackObj && callbackObj.callback) {
      callbackObj.callback(response);
    }
    setLoadChallengeModule(false);
  }
  const onCancelChallengeResponse = {
      _errorCode: 1,
      _message: "Authentication cancelled due to abort close control request.",
  };

  const oncloseChallengeModal = () => {
    setLoadChallengeModule(false);
    returnResponseBackToHost(onCancelChallengeResponse);
  }

  const logOut = (skipLogoutRedirect?: boolean, inactivity?: boolean) => {
    console.log("Clicked logout");
    if (skipLogoutRedirect) setSkipLogoutRedirect(true);
    if (inactivity) setInactivity(true);
    if(ciamAuth && ciamAuth.ciamSession && ciamAuth.ciamSession.jwtToken){
       deleteCookies(ciamAuth.ciamSession.jwtToken, `${getRouterUrl(config)}`).catch((error) =>console.error('Error deleting cookies during logout:', error) );
    }
    setLogOutLoad(true);
  }

  const logOutFunc = async (ciamSession: CIAMSession | undefined, skipLogoutRedirect?: boolean, inactivity?: boolean) => {
    console.log("Going to logout the session", ciamSession);
    if (ciamSession && ciamSession?.jwtToken) {
      try {
        await logoutSession(ciamSession.jwtToken, `${getSessionSupportUrl(config)}`);
      } catch (error) { console.error('Error logging out session:', error) }
      try {
        await deleteCookies(ciamSession.jwtToken, `${getRouterUrl(config)}`);
      } catch (error) { console.error('Error deleting cookies:', error); }
      sessionStorage.removeItem('cookie');
      channel.postMessage({ type: 'logout' });
      initOrValidateCIAMAuthSession(false, inactivity).catch((e) => {
        console.error(e);
        window.location.href = addQueryParamsIfMissing(ciamAuth.config.appLogin, { targetApp: ciamAuth.config.appId });
      });
      if (!skipLogoutRedirect && config.logoutCallBack) {
        console.log('Successfully Logged out, will call the callback');
        await config.logoutCallBack();
      }
    }
  };

  const onSessionExtend = async (token: string) => {
    console.log('Update the ciamAuth to the provider');
    try {
      await refreshSession(token);
      channel.postMessage({ type: 'refresh' });
    } catch (e) {
      console.error(e);
    }
  };

  const initOrValidateCIAMAuthSession = async (init?: boolean, inactivity?: boolean) => {
    try {
      let ciamSession: CIAMSession;
      if (!ciamAuth.config.enableCrossDomain) {
        console.log('Auth Provider: cookieResolve');
        const response = await fetch(`${getRouterUrl(config)}cookieResolve`, { credentials: 'include' });
        ciamSession = await response.json();
        console.log("CookieResolve Response", ciamSession);
      } else {
        console.log("Waiting for the iframe to send the session info");
        const cookie = sessionStorage.getItem('cookie');
        if (cookie) {
          console.log("Got Cookie from session storage", cookie);
          ciamSession = parseCookieString(cookie);
        } else {
          console.log("No cookie found in session storage, waiting for iframe to send the session info");
          setState("Loading");
          if (init) return;
          else ciamSession = {};
        }
      }
      await getUserSessionAndAction(ciamSession, inactivity);
      return ciamSession;
    } catch (e) {
      console.error('Error initializing or validating CIAM Auth session:', e);
      setState("Error");
      // window.location.href = addQueryParamsIfMissing(ciamAuth.config.appLogin, { targetApp: ciamAuth.config.appId });
    }
  }

  const replaceCookieValueFromSessionStorage = (token: string): void => {
    const cookie = sessionStorage.getItem('cookie');
    if (cookie) {
      let updatedSession;
      // If the cookie string contains jwtToken, replace its value; otherwise, append it
      if (/jwtToken=/.test(cookie)) {
        updatedSession = cookie.replace(/jwtToken=[^;]+/, `jwtToken=${token}`);
      } else {
      updatedSession = cookie.endsWith(';')
        ? `${cookie}jwtToken=${token}`
        : `${cookie};jwtToken=${token}`;
      }
      sessionStorage.setItem('cookie', updatedSession);
    } else {
      console.warn('No cookie found in session storage to update');
    }
  }

  const refreshSession = async (token: string) => {
    replaceCookieValueFromSessionStorage(token);
    await getUserSessionAndAction({ ...ciamAuth.ciamSession, jwtToken: token });
  }

  const getUserSessionAndAction = async (ciamSession: CIAMSession, inactivity?: boolean) => {
    if (ciamSession?.jwtToken) {
      const ciamUser = jwtDecode(ciamSession?.jwtToken);
      ciamSession.ciamUser = ciamUser;
      await getSession(ciamSession.jwtToken, `${getSessionSupportUrl(config)}user-session/profile`);
      setState("Loaded");
      console.log('Session Found, No redirect of url', ciamSession.ciamUser);
      setCiamAuth({ ...ciamAuth, ciamSession, logOut, loadChallenge, reverseSso, tag });
      if(!ciamSession?.users && !ciamSession?.currentSession) console.warn('No user data found in the session. Extend Session may fail.');
      if (ciamSession?.users) localStorage.setItem("users", JSON.stringify(parseUserData(ciamSession.users)));
      if (ciamSession?.currentSession) sessionStorage.setItem("currentSession", JSON.stringify(convertStringToObject(ciamSession.currentSession)));
    } else {
      console.log('Session Not Found, Redirecting to Login Page');
      setCiamAuth({ ...ciamAuth, ciamSession: undefined, logOut,reverseSso, tag });
      // window.location.href = addQueryParamsIfMissing(ciamAuth.config.appLogin, { targetApp: ciamAuth.config.appId, message: inactivity ? 'inactivity' : '' })
    }
  }

  useEffect(() => {
    if (config) {
      console.log('Preparing CIAM Session for - ', config.env);
    }
    // do this only if the current url is not in the whitelist
    if (!checkWhitelist()) {
      initOrValidateCIAMAuthSession(true)
      .then((ciamSession) => {
        ciamAuth.config.initCallBack && ciamAuth.config.initCallBack(ciamSession as CIAMSession);
      }).catch((e) => {
        console.error('Failed to fetch user session. Navigate to Login page', e);
        setState("Error");
        window.location.href = addQueryParamsIfMissing(ciamAuth.config.appLogin, { targetApp: ciamAuth.config.appId });
      });
    } else {
      console.log('Current URL is in the whitelist, skipping CIAM session initialization.');
      setCiamAuth({ ...ciamAuth, ciamSession: undefined, logOut, reverseSso, tag });
    }
  }, []);

  useEffect(() => {
    function handleMessage(event: any) {
      if (event.data.type === 'CIAM_SESSION_INFO') {
        console.log('Received from iframe:', event.data.payload);
        sessionStorage.setItem('cookie', event.data.payload);
        initOrValidateCIAMAuthSession(true)
        .then((ciamSession) => {
          ciamAuth.config.initCallBack && ciamAuth.config.initCallBack(ciamSession as CIAMSession);
        }).catch((e) => {
          console.error('Failed to fetch user session from iframe. Navigate to Login page', e);
          setState("Error");
          window.location.href = ciamAuth.config.appLogin + '?targetApp=' + ciamAuth.config.appId;
        });
      }
      if (event.data.type === 'CIAM_SESSION_IFRAME') {
        console.log('Received iframe visibility message:', event.data.showIframe);
        setShowIframe(event.data.showIframe);
      }
      if (event.data && event.data.type === 'refresh' || event.data && event.data.type === 'logout') {
        initOrValidateCIAMAuthSession(false)
        .then(ciamSession => {
          if (event.data.type === 'logout' && (!ciamSession || !ciamSession?.jwtToken)) {
            if (config.logoutCallBack)  config.logoutCallBack();
          }
        }).catch((e) => {
          console.error('Failed to refresh or logout session from another tab', e);
          setState("Error");
        });
      }
    }
    channel.addEventListener('message', handleMessage);
    window.addEventListener('message', handleMessage);
    return () => {
      channel.removeEventListener('message', handleMessage);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const checkDomains = (): boolean => {
    const allowedBaseDomains = ['avvance.usbank.com', 'elavon.com', 'avvance.com', 'us.bank-dns.com', 'localhost'];
    const hostname = window.location.hostname;
    return allowedBaseDomains.some(base =>
      hostname === base || hostname.endsWith(`.${base}`)
    );
  };

  useEffect(() => {
    if (ciamAuth?.config?.enableCrossDomain && checkDomains()) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [ciamAuth?.config?.enableCrossDomain, ciamAuth?.config]);

  return (
    <CIAMAuthContext.Provider value={ciamAuth}>
      {/* Modal popup for iframe */}
      {showModal && (
        <div style={iframeStyles}>
          <iframe
            src={`${getRouterUrl(ciamAuth.config)}cross`}
            title="Cross Domain Iframe"
            height="100%"
            width="100%"
          />
        </div>
      )}

      {ciamAuth?.config?.showLoading && state === 'Loading' && (
        <div className='spinner-overlay'>
          <USBLoadingSpinner />
        </div>
      )}
      {state !== 'Loading' && (
        <>
          {ciamAuth?.ciamSession?.ciamUser ? (
            <>
              <KeepLive onSessionExtend={onSessionExtend} onSessionValidate={initOrValidateCIAMAuthSession} logOut={logOut} logOutLoad={logOutLoad} />
              {logOutLoad && (<LogoutModuleLoader logOutFunc={logOutFunc} skipLogoutRedirect={skipLogoutRedirect} inactivity={inactivity} />)}
              {loadChallengeModule &&
                <CIAMAuthModal
                  showModal={loadChallengeModule}
                  onModalClose={oncloseChallengeModal}
                  onAuthSuccess={onChallengeAuthSuccess}
                  onAuthFailure={onChallengeAuthFailure}
                />}
            </>
          ) : (
            <></>
          )}
        </>
      )}
      {children}
    </CIAMAuthContext.Provider>
  );
};

export default CIAMAuthProvider;
